/**
 * Thin fetch wrapper around the worker API. All endpoints under /api/v1.
 * In dev the Vite proxy forwards /api to localhost:8787 (see vite.config.ts).
 *
 * Returns parsed JSON or throws an ApiError with the status + RFC 7807 body.
 */
import type { BilingualText, Concept, Diagram, Relation, ViewState } from "@ae/domain";

export interface ApiErrorBody {
  type: string;
  title: string;
  status: number;
  detail?: string;
  errors?: Array<{ path: string; code: string; message: string }>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorBody | null;
  constructor(status: number, body: ApiErrorBody | null, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/**
 * Globale hook die wordt aangeroepen na elke succesvolle workspace-
 * mutatie. Wordt door `apps/web/src/realtime/session.ts` ingericht
 * zodat de realtime-laag een "ik heb iets veranderd"-signaal naar
 * de DO kan sturen voor peer-rehydrate. Default = no-op.
 */
let mutationHook: ((path: string, method: string) => void) | null = null;

export function setApiMutationHook(hook: ((path: string, method: string) => void) | null): void {
  mutationHook = hook;
}

/** Pad-regex voor mutaties die peers moeten zien — concepten,
 *  relaties, diagrammen onder een workspace. View-kinds en admin-
 *  acties vallen voorlopig buiten beschouwing. */
const MUTATION_PATH_RE = /^\/api\/v1\/workspaces\/[^/]+\/(concepts|relations|diagrams)(\/|$)/;

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  query?: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const url = new URL(path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) {
        url.searchParams.set(k, String(v));
      }
    }
  }

  const headers: Record<string, string> = { accept: "application/json" };
  const init: RequestInit = {
    method,
    credentials: "include",
    headers,
  };
  if (body !== undefined) {
    headers["content-type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  const res = await fetch(url.toString(), init);

  if (res.status === 204) {
    notifyMutation(method, path, true);
    return undefined as T;
  }

  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }

  if (!res.ok) {
    const errBody = (parsed as ApiErrorBody | null) ?? null;
    throw new ApiError(
      res.status,
      errBody,
      errBody?.detail ?? errBody?.title ?? `HTTP ${res.status}`,
    );
  }
  notifyMutation(method, path, true);
  return parsed as T;
}

/**
 * Dezelfde aanroep als `@ae/api-client` verwacht, met de cookie-sessie van
 * de web-app. Voor functies die web en desktop delen (workspace-archief).
 */
export const webRequester = {
  request<T>(
    method: string,
    path: string,
    opts?: { query?: Record<string, string | number | boolean | null>; body?: unknown },
  ): Promise<T> {
    const query = opts?.query
      ? Object.fromEntries(
          Object.entries(opts.query).filter(
            (e): e is [string, string | number | boolean] => e[1] !== null,
          ),
        )
      : undefined;
    return request<T>(method, path, opts?.body, query);
  },
};

function notifyMutation(method: string, path: string, ok: boolean): void {
  if (!ok) return;
  if (method === "GET" || method === "HEAD" || method === "OPTIONS") return;
  if (!mutationHook) return;
  if (!MUTATION_PATH_RE.test(path)) return;
  try {
    mutationHook(path, method);
  } catch (err) {
    console.error("[api] mutation hook threw", err);
  }
}

// ---- Endpoints -----------------------------------------------------------

/** Wire shape for a workspace-scoped custom view-kind. */
export type ApiTokenScope = "read" | "model" | "admin";

export interface ApiTokenDTO {
  id: string;
  name: string;
  token_prefix: string;
  scope: ApiTokenScope;
  workspace_id: string | null;
  client: string;
  /** AI-koppeling schrijft alleen op eigen AI-plateaus (migratie 0062). */
  ai_plateau_only?: boolean;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface DeviceApprovalDTO {
  user_code: string;
  client: string;
  client_name: string;
  requested_scope: ApiTokenScope;
  workspace_id: string | null;
  workspace_name: string | null;
  /** De koppeling komt van de MCP-server (een AI). */
  is_ai?: boolean;
  expires_at: string;
}

export interface CustomViewKindDTO {
  id: string;
  workspaceId: string;
  name: { nl?: string; en?: string };
  description: { nl?: string; en?: string } | null;
  includedTypes: string[];
  includedRelationTypes: string[] | null;
  creatorEmail: string;
  centrallyManaged: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomViewKindBody {
  name_nl: string;
  name_en: string;
  description_nl?: string | null;
  description_en?: string | null;
  included_types: string[];
  included_relation_types?: string[] | null;
}

export interface PatchCustomViewKindBody {
  name_nl?: string;
  name_en?: string;
  description_nl?: string | null;
  description_en?: string | null;
  included_types?: string[];
  included_relation_types?: string[] | null;
  centrally_managed?: boolean;
}

export interface MeResponse {
  email: string;
  ui_language: string;
  /** ArchiMate-weergavemodus — `"v31"` of `"v4"`, server-default `"v31"`.
   *  Puur presentatie: het model op de server is er maar één. Zie
   *  `state/displayMode.ts`. */
  display_mode: string;
  workspaces: Array<{
    workspace_id: string;
    workspace_name: string;
    role: "member" | "admin";
    teams: string[];
  }>;
  is_super_admin: boolean;
  super_admin_override: { workspace_id: string; workspace_name: string } | null;
}

/** Soort object in een samenvoeging. */
export type MergeObjectKind = "concept" | "relation" | "diagram";
export type MergeSide = "source" | "target";

/** Wat de proefrun van een samenvoeging teruggeeft (`POST /branches/:id/merge`). */
export interface MergePreview {
  dry_run: boolean;
  source: { id: string; name: string; workpackageId: string | null };
  target: { id: string; name: string; isMain: boolean };
  /** De directe kinderen van de branch: die krijgen de ouder als ouder. */
  children: Array<{ id: string; name: string }>;
  subtree: string[];
  counts: Record<MergeObjectKind, { added: number; changed: number; deleted: number }>;
  actions: Record<string, number>;
  renamed_ids: number;
  conflicts: Array<{
    kind: MergeObjectKind;
    logicalId: string;
    name: unknown;
    type: "fields" | "deletedOnTarget" | "changedOnTarget";
    fields?: Array<{
      path: string;
      base: unknown;
      target: unknown;
      source: unknown;
      resolution: MergeSide;
    }>;
    resolution?: MergeSide;
  }>;
  conflicts_truncated: boolean;
  changes: Array<{
    kind: MergeObjectKind;
    id: string;
    name: unknown;
    changes: Array<{ path: string }>;
  }>;
  changes_truncated: boolean;
  duplicates: Array<{ kind: MergeObjectKind; logicalId: string; kept: string; dropped: string[] }>;
  /** Per andere branch wat die overneemt en houdt (M9). */
  propagation: Array<{
    branchId: string;
    kind: MergeObjectKind;
    logicalId: string;
    name: unknown;
    status: "updated" | "rebased" | "deletedOnParent" | "noBase";
    taken: string[];
    kept: string[];
  }>;
  propagation_truncated: boolean;
  branch_names?: Record<string, string>;
  /** Alleen na uitvoeren. */
  merge_id?: string;
  safety_snapshot_id?: string;
}

/** Weigering vooraf: waarom deze branch niet kan opgaan in z'n ouder. */
export interface MergeRefusal {
  refused: true;
  code: string;
  message: string;
}

export interface BranchSummary {
  id: string;
  workspace_id: string;
  name: string;
  parent_branch_id: string | null;
  fork_point_snapshot_id: string | null;
  status: "active" | "merged" | "abandoned";
  head_snapshot_id: string | null;
  created_at: string;
  created_by: string;
  /** True wanneer deze branch de main-rol heeft voor z'n workpackage
   *  (computed server-side via workpackages.main_branch_id). Vanaf M6.5
   *  is "main" een rol per workpackage, niet langer een gereserveerde
   *  naam. Een workspace kan dus N main-branches hebben (één per WP). */
  is_main: boolean;
  /** Het gekoppelde Plateau-concept (roadmap) — null voor gewone branches. */
  plateau_concept_id: string | null;
  /** Bilinguale naam van dat Plateau-concept; bron voor de meertalige
   *  branch-weergave (toon de actieve-taal-variant). Null = geen plateau. */
  plateau_name: BilingualText | null;
  /** Migratie 0060: `mcp` = door een AI aangemaakt; null = in de browser. */
  created_via_client?: string | null;
}

/** Alias voor BranchSummary — semantisch identiek, gebruikt door
 *  de §3.6 branch-CRUD endpoints. */
export type BranchRow = BranchSummary;

/** Een plateau-lane in de Roadmap (Stap 3c). Komt van GET /branches/plateaus
 *  als domein-`Branch` (camelCase). Plateau 1 = de canonieke main (= "nu"),
 *  ook als die nog geen `plateauConceptId` heeft. */
export interface PlateauBranch {
  id: string;
  name: string;
  parentBranchId?: string;
  /** Het gekoppelde `archimate.Plateau`-concept; afwezig op de impliciete
   *  main-Plateau-1 tot ensure-create (Stap 3d) 'm linkt. */
  plateauConceptId?: string;
  isMain?: boolean;
  /** Migratie 0060: `mcp` = door een AI aangemaakt. */
  createdViaClient?: string;
  /** Volgorde van zusters op de roadmap: het oudste pad bovenaan. */
  createdAt?: string;
}

// ── M6 Explorer — DTO's voor de nieuwe organisatie-laag ─────────────

export type RetiredScopeParam =
  | { kind: "workspace" }
  | { kind: "workpackage"; workpackageId: string };

export interface RetiredPreviewDTO {
  concepts: Array<{ id: string; type_id: string; name: BilingualText }>;
  relations: Array<{
    id: string;
    type_id: string;
    source_concept_id: string;
    target_concept_id: string;
  }>;
  diagrams: Array<{ id: string; name: string }>;
  cascade_relation_count: number;
  cascade_viewstate_entry_count: number;
  cascade_viewstate_diagram_count: number;
}

export interface RetiredCleanupResultDTO {
  concepts_tombstoned: number;
  relations_tombstoned: number;
  relations_cascaded: number;
  diagrams_deleted: number;
  viewstate_entries_stripped: number;
  viewstate_diagrams_touched: number;
}

export interface WorkPackageDTO {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  status: "open" | "closed";
  mainBranchId?: string;
  /** True voor de speciale Base-workpackage (M6.5-W2). */
  isBase: boolean;
  /** Wat een AI (MCP-koppeling) hier mag schrijven (migratie 0062). */
  aiWrite?: AiWriteMode;
  /** Wat de ingelogde gebruiker hier mag; bepaalt of de instellingen te wijzigen zijn. */
  myAccess?: "admin" | "write" | "read";
  createdBy: string;
  createdAt: string;
}

export type AiWriteMode = "none" | "ai_plateau" | "anywhere";

export type FolderTypeGroup = "archimate" | "sharp" | "bmc" | "c4";

export interface FolderDTO {
  id: string;
  workpackageId: string;
  name: string;
  description?: string;
  /** M6.5-W3b — type-klasse die in deze folder mag landen (immutable). */
  typeGroup: FolderTypeGroup;
  createdBy: string;
  createdAt: string;
}

export type FolderAccessMode = "read" | "write";

export interface FolderAccessDTO {
  workpackageId: string;
  folderId: string;
  mode: FolderAccessMode;
  grantedBy: string;
  grantedAt: string;
}

export interface TeamDTO {
  id: string;
  workspaceId: string;
  name: string;
  description?: string;
  foundingMember?: string;
  createdByAdmin?: string;
  /** True voor het speciale Admin-team waarvan leden auto-admin worden
   *  op workspace_members (M6.5). Eén per workspace, immutable in UI. */
  isAdmin: boolean;
  createdAt: string;
}

export interface ExplorerSubGroup<T> {
  typeId: string;
  count: number;
  items: T[];
}

export interface ExplorerConceptItem {
  id: string;
  typeId: string;
  /** Bilingual — frontend resolves via resolveBilingual(name, lang). */
  name: BilingualText;
  /** Echte branch-ownership (null = pre-M6 legacy = main). */
  branchId: string | null;
  signal: "healthy" | "orphan";
  tombstoneInBranch: boolean;
  /** M6.5-W3b — concept is een Reference Object (zit in een Base-folder). */
  inFolder?: boolean;
  folderName?: string;
  /** Model-niveau eigenaar (Sharp: Attribuut/Identifier → Concept). De Explorer
   *  nest deze items onder hun Concept i.p.v. als losse type-groep. */
  parentId?: string | null;
}

export interface ExplorerDiagramItem {
  id: string;
  viewKind: string;
  /** Bilingual — frontend resolves via resolveBilingual(name, lang). */
  name: BilingualText;
  /** Echte branch-ownership van het diagram. */
  branchId: string;
  signal: "healthy" | "empty_diagram";
}

export interface ExplorerLayer {
  layer: "archimate" | "c4" | "sharp" | "bmc" | "drawing";
  total: number;
  objectGroups: ExplorerSubGroup<ExplorerConceptItem>[];
  diagramGroups: ExplorerSubGroup<ExplorerDiagramItem>[];
}

export interface ExplorerTreeDTO {
  workpackage: { id: string; name: string; status: "open" | "closed" };
  branch: { id: string; name: string; status: string };
  layers: ExplorerLayer[];
}

export interface DiagramOccurrenceDTO {
  diagramId: string;
  /** Bilingual — frontend resolves via resolveBilingual. */
  diagramName: BilingualText;
  viewKind: string;
  branchId: string;
}

export interface ObjectDetailDTO {
  concept: Concept;
  relations: Relation[];
  diagrams: DiagramOccurrenceDTO[];
  /** M6.5-W3b — naam-resolver voor counterpart-concepten die mogelijk
   *  niet in de workspace-store-hydrate zaten (folder-concepten op
   *  Base.main wanneer caller op een non-Base branch is). */
  relatedConcepts?: Array<{ id: string; name: BilingualText }>;
}

export interface DiagramDetailDTO {
  diagram: {
    id: string;
    /** Bilingual — frontend resolves via resolveBilingual. */
    name: BilingualText;
    viewKind: string;
    branchId: string;
    createdAt: string;
    updatedAt: string;
  };
  objects: Array<{
    id: string;
    typeId: string;
    /** Bilingual — frontend resolves via resolveBilingual. */
    name: BilingualText;
  }>;
}

/** Branch-diff DTO's (roadmap-tijdreizen.md stap 3d). Spiegelt de
 *  worker-output van diffBranch* (apps/worker/src/db/branchDiff.ts). */
export type BranchDiffStatus = "added" | "changed" | "deleted";
export interface BranchDiffField {
  field: string;
  before: unknown;
  after: unknown;
}
/** Weergave-metadata voor een relatie-diff-entity (bron → glyph → doel). */
export interface BranchDiffRelationMeta {
  kind: "relation";
  typeId: string;
  attributes?: Record<string, unknown>;
  stereotypes?: string[];
  sourceLabel?: { nl?: string; en?: string };
  targetLabel?: { nl?: string; en?: string };
  /** Logische endpoint-id's (origin_id ?? id) voor de Gap-model-graaf. */
  sourceConceptId?: string;
  targetConceptId?: string;
}
/** Weergave-metadata voor een object-diff-entity (naam + type). */
export interface BranchDiffConceptMeta {
  kind: "concept";
  typeId: string;
  stereotypes?: string[];
  name?: { nl?: string; en?: string };
  /** Logische id van het eigenaar-Concept (voor Attribuut/Identifier-nesting). */
  parentId?: string | null;
  /** Naam van het eigenaar-Concept, zodat de Gap een carrier-box kan tonen. */
  parentName?: { nl?: string; en?: string };
}
export interface BranchDiffEntity {
  logicalId: string;
  status: BranchDiffStatus;
  label?: string;
  meta?: BranchDiffRelationMeta | BranchDiffConceptMeta | Record<string, unknown>;
  deltas: BranchDiffField[];
}
/** Eén cel in het Plateau-diagram-grid (roadmap-tijdreizen §6.2). */
export interface PlateauDiagramCell {
  id: string;
  logicalId: string;
  name: { nl?: string; en?: string };
  label: string;
  viewKind: string;
  status: "added" | "changed" | "deleted" | "neutral";
  /** logicalIds van gewijzigde concepten/relaties die op dit diagram staan. */
  changedConceptIds: string[];
  changedRelationIds: string[];
}
export interface BranchDiffResponse {
  concepts: BranchDiffEntity[];
  relations: BranchDiffEntity[];
  diagrams: BranchDiffEntity[];
  /** Volledige diagram-grid (incl. neutrale) voor het Plateau-diagram. */
  diagramGrid?: PlateauDiagramCell[];
  /** Huidige branch (after-kolom). */
  branch?: { id: string; name: string; isMain?: boolean } | null;
  /** Vorig plateau / parent-branch (before-kolom). */
  parent?: { id: string; name: string; isMain?: boolean } | null;
}

/** Eén object in de voorspelling — met de naam zoals 'ie nú in het model
 *  staat, dus wat je kwijtraakt of ziet veranderen. */
export interface PreviewItem {
  id: string;
  name: string;
  typeId?: string;
}

export interface PreviewDiagram {
  id: string;
  name: string;
  action: "update" | "insert" | "remove";
  placements: number;
}

/**
 * Wat een import of restore gaat doen, vóórdat 'ie het doet. Nieuw is een
 * getal; overschrijven en verdwijnen zijn lijsten. Zie
 * `docs/architecture/gelijktijdigheid.md` §7.
 */
export interface OperationPreview {
  kind: "import" | "restore" | "merge";
  created: { concepts: number; relations: number; diagrams: number; folders: number };
  overwritten: { concepts: PreviewItem[]; relations: PreviewItem[] };
  overwritten_counts: { concepts: number; relations: number };
  removed: { concepts: PreviewItem[]; relations: PreviewItem[] };
  removed_counts: { concepts: number; relations: number };
  diagrams: PreviewDiagram[];
  reused: number;
  truncated: boolean;
}

/**
 * Een aangekondigde import of restore, zoals de server 'm teruggeeft. De fase
 * komt van de server mee: twee schermen met een scheve klok horen niet elk iets
 * anders te concluderen.
 */
export interface PendingOperationView {
  id: string;
  kind: "import" | "restore" | "merge";
  status: "announced" | "quiet" | "running" | "done" | "cancelled";
  requestedBy: string;
  label: string;
  impact: Record<string, number> | null;
  /** De droge run zoals de aanvrager 'm zag; `null` bij een operatie zonder
   *  voorspelling (AMEFF-import bijvoorbeeld). */
  preview: OperationPreview | null;
  startsAt: string;
  workpackageIds: string[];
  /** Alleen deze branches (het terugzetten van een branch-snapshot);
   *  ontbreekt = de hele workpackages. */
  branchIds?: string[];
  audience: string[];
  acks: Array<{ email: string; at: string }>;
  extensions: Array<{ email: string; at: string }>;
  awaiting: string[];
  phase: "countdown" | "quiet" | "due";
  quiet_minutes: number;
}

export const api = {
  me: () => request<MeResponse>("GET", "/api/v1/me"),
  patchMe: (body: { ui_language?: "nl" | "en"; display_mode?: "v31" | "v4" }) =>
    request<MeResponse>("PATCH", "/api/v1/me", body),
  /** Diff van een branch t.o.v. z'n directe voorganger (vorig plateau). */
  branchDiff: (wsId: string, branchId: string) =>
    request<BranchDiffResponse>("GET", `/api/v1/workspaces/${wsId}/branches/${branchId}/diff`),
  /**
   * Nasynchroniseer de branch-eigen diagrammen met hun parent: trekt
   * plaatsingen over die de parent ná de fork toevoegde. Idempotent +
   * timestamp-gated; aangeroepen bij branch-switch. Returnt welke diagrammen
   * entries kregen zodat de caller die kan herhydrateren.
   */
  reconcileBranch: (wsId: string, branchId: string) =>
    request<{
      reconciled: Array<{ diagramId: string; addedKeys: string[]; updatedKeys: string[] }>;
      parentDeletedPrompts: Array<{
        conceptId: string;
        label: { nl?: string; en?: string };
        typeId: string;
        relationIds: string[];
      }>;
      homonymPrompts: Array<{
        branchConceptId: string;
        parentConceptId: string;
        typeId: string;
        name: { nl?: string; en?: string };
      }>;
    }>("POST", `/api/v1/workspaces/${wsId}/branches/${branchId}/reconcile`),
  /**
   * Resolutie van een parent-delete-investering (Fase 2). `accept` = ook hier
   * verwijderen (no-op; al getombstoned). `keep` = R behouden als branch-object
   * — resurrect + herwijs de branch-local relaties; returnt het nieuwe
   * concept-id zodat de client kan herhydrateren.
   */
  resolveParentDelete: (
    wsId: string,
    branchId: string,
    body: { conceptId: string; action: "accept" | "keep"; relationIds: string[] },
  ) =>
    request<{ ok: boolean; newConceptId?: string }>(
      "POST",
      `/api/v1/workspaces/${wsId}/branches/${branchId}/resolve-parent-delete`,
      body,
    ),
  /**
   * Fase-3: koppel een homoniem-paar — adopteer de parent-identiteit op het
   * branch-concept (`origin_id`). Branch-divergentie blijft volledig heel.
   */
  linkHomonym: (
    wsId: string,
    branchId: string,
    body: { branchConceptId: string; parentConceptId: string },
  ) =>
    request<{ ok: boolean; alreadyLinked?: boolean }>(
      "POST",
      `/api/v1/workspaces/${wsId}/branches/${branchId}/link-homonym`,
      body,
    ),
  /** Fase-3: negeer een homoniem-paar permanent op deze branch. */
  dismissHomonym: (
    wsId: string,
    branchId: string,
    body: { branchConceptId: string; parentConceptId: string },
  ) =>
    request<{ ok: boolean }>(
      "POST",
      `/api/v1/workspaces/${wsId}/branches/${branchId}/dismiss-homonym`,
      body,
    ),
  listConcepts: (wsId: string, branchId: string) =>
    request<{ items: Concept[]; next_cursor: string | null }>(
      "GET",
      `/api/v1/workspaces/${wsId}/concepts`,
      undefined,
      { branch_id: branchId },
    ),
  createConcept: (wsId: string, branchId: string, body: unknown) =>
    request<Concept>("POST", `/api/v1/workspaces/${wsId}/concepts`, body, { branch_id: branchId }),
  patchConcept: (wsId: string, branchId: string, id: string, body: unknown) =>
    request<Concept>("PATCH", `/api/v1/workspaces/${wsId}/concepts/${id}`, body, {
      branch_id: branchId,
    }),
  /** Soft-delete één concept.
   *
   *  `actionId` is optioneel — bij bulk-delete geeft de UI één gedeelde
   *  UUID mee aan alle individuele DELETEs zodat de Prullenbak ze als
   *  één entry kan tonen (multi-delete-groepering). */
  deleteConcept: (
    wsId: string,
    branchId: string,
    id: string,
    confirmImpact?: string,
    actionId?: string,
  ) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/concepts/${id}`, undefined, {
      branch_id: branchId,
      confirm_impact: confirmImpact,
      ...(actionId ? { action_id: actionId } : {}),
    }),
  /**
   * Voeg ≥1 loser-concepten samen in een winner. Server-side worden alle
   * relaties + viewState-referenties verlegd en de losers verwijderd.
   * Returnt de bijgewerkte winner.
   */
  mergeConcepts: (wsId: string, branchId: string, winnerId: string, loserIds: string[]) =>
    request<Concept>(
      "POST",
      `/api/v1/workspaces/${wsId}/concepts/${winnerId}/merge`,
      { loser_ids: loserIds },
      { branch_id: branchId },
    ),
  /** Merge loser-relaties in één winner (duplicaat-detectie + rechtermuis-merge). */
  mergeRelations: (wsId: string, branchId: string, winnerId: string, loserIds: string[]) =>
    request<Relation>(
      "POST",
      `/api/v1/workspaces/${wsId}/relations/${winnerId}/merge`,
      { loser_ids: loserIds },
      { branch_id: branchId },
    ),
  /**
   * Read-only cascade-impact for a concept. Driven by the
   * confirmation-dialog before delete: the UI shows the diagrams + relations
   * that will be affected so the user knows what they're about to break.
   */
  cascadePreviewConcept: (wsId: string, branchId: string, id: string) =>
    request<{
      concept: { id: string; name: BilingualText; type_id: string };
      summary: { diagram_count: number; relation_count: number };
      diagrams: Array<{ id: string; name: BilingualText }>;
      relations: Array<{
        id: string;
        type_id: string;
        other_concept_id: string;
        direction: "outgoing" | "incoming";
      }>;
    }>("GET", `/api/v1/workspaces/${wsId}/concepts/${id}/cascade-preview`, undefined, {
      branch_id: branchId,
    }),

  /** Lijst alle versies van een concept, nieuwste eerst. */
  listConceptHistory: (wsId: string, branchId: string, conceptId: string) =>
    request<{ versions: VersionEntry<Concept>[] }>(
      "GET",
      `/api/v1/workspaces/${wsId}/concepts/${conceptId}/history`,
      undefined,
      { branch_id: branchId },
    ),
  /** Zet een eerdere versie terug als 'current'. Schrijft een nieuwe versie-rij. */
  restoreConceptVersion: (
    wsId: string,
    branchId: string,
    conceptId: string,
    versionNumber: number,
  ) =>
    request<Concept>(
      "POST",
      `/api/v1/workspaces/${wsId}/concepts/${conceptId}/restore-version`,
      { version_number: versionNumber },
      { branch_id: branchId },
    ),

  listRelations: (wsId: string, branchId: string) =>
    request<{ items: Relation[]; next_cursor: string | null }>(
      "GET",
      `/api/v1/workspaces/${wsId}/relations`,
      undefined,
      { branch_id: branchId },
    ),
  createRelation: (wsId: string, branchId: string, body: unknown) =>
    request<Relation>("POST", `/api/v1/workspaces/${wsId}/relations`, body, {
      branch_id: branchId,
    }),
  patchRelation: (wsId: string, branchId: string, id: string, body: unknown) =>
    request<Relation>("PATCH", `/api/v1/workspaces/${wsId}/relations/${id}`, body, {
      branch_id: branchId,
    }),
  /** Soft-delete één relatie. Zie `deleteConcept` voor `actionId`. */
  deleteRelation: (wsId: string, branchId: string, id: string, actionId?: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/relations/${id}`, undefined, {
      branch_id: branchId,
      ...(actionId ? { action_id: actionId } : {}),
    }),

  listRelationHistory: (wsId: string, branchId: string, relationId: string) =>
    request<{ versions: VersionEntry<Relation>[] }>(
      "GET",
      `/api/v1/workspaces/${wsId}/relations/${relationId}/history`,
      undefined,
      { branch_id: branchId },
    ),
  restoreRelationVersion: (
    wsId: string,
    branchId: string,
    relationId: string,
    versionNumber: number,
  ) =>
    request<Relation>(
      "POST",
      `/api/v1/workspaces/${wsId}/relations/${relationId}/restore-version`,
      { version_number: versionNumber },
      { branch_id: branchId },
    ),

  listDiagrams: (wsId: string, branchId: string) =>
    request<Diagram[]>("GET", `/api/v1/workspaces/${wsId}/diagrams`, undefined, {
      branch_id: branchId,
    }),
  createDiagram: (
    wsId: string,
    branchId: string,
    body: {
      name: BilingualText;
      notation_pack: string;
      view_kind: string;
    },
  ) =>
    request<Diagram>("POST", `/api/v1/workspaces/${wsId}/diagrams`, body, { branch_id: branchId }),
  patchDiagram: (
    wsId: string,
    branchId: string,
    id: string,
    body: {
      name?: BilingualText;
      description?: BilingualText | null;
      /** De naam die wij dachten te vervangen; laat de server een gelijktijdige
       *  hernoeming weigeren. */
      name_was?: BilingualText;
      view_state?: ViewState;
      /** De rij-versie die wij in handen hadden; laat de server de
       *  vergelijking per sleutel overslaan als er sindsdien niets geschreven
       *  is. Zie `viewStateMerge.ts`. */
      row_version?: number;
      /** Sleutels die wij hebben weggehaald. De server voegt viewState samen
       *  in plaats van te vervangen, dus weglaten betekent niet verwijderen. */
      view_state_removed?: string[];
      view_kind?: string;
      attributes?: Record<string, unknown>;
    },
  ) =>
    // Server kan een copy-on-write kopie maken (zie ensureBranchOwnedDiagram).
    // Dan retourneert 'ie het nieuwe Diagram-record (met nieuwe id) plus
    // `branchCopied: true` en `requestedId` zodat de client weet dat 't 'm
    // moet switchen.
    request<
      Diagram & {
        branchCopied?: boolean;
        requestedId?: string;
        /** Voorkomens die iemand anders intussen wijzigde; die zijn niet
         *  geschreven. Wat wél landde staat er gewoon. */
        viewStateRejected?: string[];
      }
    >("PATCH", `/api/v1/workspaces/${wsId}/diagrams/${id}`, body, { branch_id: branchId }),
  deleteDiagram: (wsId: string, branchId: string, id: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/diagrams/${id}`, undefined, {
      branch_id: branchId,
    }),
  // Undo van een delete: herstelt het diagram met dezelfde id (un-tombstone of
  // her-insert). Body = de volledige diagram-inhoud.
  restoreDiagram: (
    wsId: string,
    branchId: string,
    id: string,
    body: {
      name: BilingualText;
      description?: BilingualText | null;
      notation_pack: string;
      view_kind: string;
      view_state: ViewState;
      attributes: Record<string, unknown>;
    },
  ) =>
    request<Diagram>("POST", `/api/v1/workspaces/${wsId}/diagrams/${id}/restore`, body, {
      branch_id: branchId,
    }),

  // ---- Custom view-kinds (workspace-scoped diagram type definitions) ----
  listCustomViewKinds: (wsId: string) =>
    request<CustomViewKindDTO[]>("GET", `/api/v1/workspaces/${wsId}/view-kinds`),
  createCustomViewKind: (wsId: string, body: CreateCustomViewKindBody) =>
    request<CustomViewKindDTO>("POST", `/api/v1/workspaces/${wsId}/view-kinds`, body),
  patchCustomViewKind: (wsId: string, id: string, body: PatchCustomViewKindBody) =>
    request<CustomViewKindDTO>("PATCH", `/api/v1/workspaces/${wsId}/view-kinds/${id}`, body),
  deleteCustomViewKind: (wsId: string, id: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/view-kinds/${id}`),

  // ---- Concept subtypes (workspace-scoped Specialization extensions) ----
  translate: (body: {
    source_lang: "nl" | "en";
    target_lang: "nl" | "en";
    text: string;
    kind?: "name" | "description";
  }) => request<{ text: string }>("POST", "/api/v1/translate", body),
  /**
   * Auto-detect-variant: detecteert de brontaal (nl/en) en vertaalt naar de
   * andere. Retourneert de gedetecteerde `source_lang` zodat de caller de input
   * onder de juiste taal kan opslaan en de vertaling onder de andere.
   */
  translateAuto: (text: string, kind?: "name" | "description") =>
    request<{ text: string; source_lang: "nl" | "en"; target_lang: "nl" | "en" }>(
      "POST",
      "/api/v1/translate",
      { source_lang: "auto", target_lang: "auto", text, ...(kind ? { kind } : {}) },
    ),

  /**
   * Pipe a diagram-SVG through Claude for redesign / simplification.
   * The worker uses the AI provider of the installation. See
   * `apps/worker/src/handlers/aiTransform.ts` for the full flow.
   */
  aiTransform: (body: {
    svg: string;
    /** ArchiMate-PlantUML — semantic source-of-truth, fully typed. */
    plantuml: string;
    /** JSON-text digest of concepts + relations with attributes. */
    summary: string;
    prompt: string;
    output_mode: "svg" | "json" | "plantuml";
    /** Defaults to claude-opus-4-7 server-side. */
    model?: string;
    /**
     * Optionele PNG-base64 (zonder data:-URL-prefix) van het canvas.
     * Wanneer aanwezig stuurt de worker 'm als `image`-content-block
     * naast de tekst — Claude tokeniseert images vrijwel constant
     * (~1500 tokens) ongeacht grootte, dus dit ontwijkt de context-
     * window-bottleneck van inline-SVG-tekst.
     */
    image_base64?: string;
    image_media_type?: "image/png" | "image/jpeg";
  }) =>
    request<{
      output_mode: "svg" | "json" | "plantuml";
      model: string;
      content: string;
      extracted: boolean;
      raw: string;
      stop_reason: string | null;
    }>("POST", "/api/v1/ai/transform", body),

  /**
   * Zet een natuurlijk-taal-beschrijving om naar overlay-regels (conditional
   * formatting) via Claude. `context` grondt de LLM op de
   * daadwerkelijk beschikbare types/stereotypes/lagen/attributen/attribuut-
   * typen; de teruggekomen regels worden client-side gesaneerd vóór gebruik.
   * Zie `apps/worker/src/handlers/aiOverlayRules.ts`.
   */
  suggestOverlayRules: (body: {
    prompt: string;
    context: {
      layers?: string[];
      types?: { id: string; label: string }[];
      stereotypes?: { id: string; label: string }[];
      attributes?: { name: string; label: string; type: string; options?: string[] }[];
      attributeTypes?: { id: string; name: string }[];
    };
    model?: string;
  }) => request<{ rules: unknown[] }>("POST", "/api/v1/ai/overlay-rules", body),

  /**
   * Render diagram-source to an image via the worker proxy. Returns the
   * raw bytes plus the content-type — caller decides whether to embed
   * the SVG inline (`URL.createObjectURL` for an `<img>`, `<object>`,
   * or use `text/svg+xml` direct), or download the PNG.
   *
   * The endpoint is thin around Kroki — see `apps/worker/src/handlers/render.ts`.
   * Cache-Control on the response is `immutable + 1y`, so repeated
   * calls with the same source will be near-instant on the second hit.
   */
  render: async (body: {
    source: string;
    format: "plantuml" | "mermaid";
    outputType: "svg" | "png";
  }): Promise<{ blob: Blob; contentType: string; cached: boolean }> => {
    const res = await fetch("/api/v1/render", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", accept: "image/*" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let parsed: ApiErrorBody | null = null;
      try {
        parsed = text ? (JSON.parse(text) as ApiErrorBody) : null;
      } catch {
        parsed = null;
      }
      throw new ApiError(
        res.status,
        parsed,
        parsed?.detail ?? parsed?.title ?? `HTTP ${res.status}`,
      );
    }
    const blob = await res.blob();
    return {
      blob,
      contentType: res.headers.get("content-type") ?? blob.type,
      cached: (res.headers.get("x-cache") ?? "").toUpperCase() === "HIT",
    };
  },

  getUserSettings: () =>
    request<{
      ai_model_preference: string | null;
      ai_effort_preference?: "low" | "medium" | "high" | null;
    }>("GET", "/api/v1/users/me/settings"),
  /** Hoe lang "Vraag Claude" standaard nadenkt; `null` = standaard (medium). */
  setAiEffort: (effort: "low" | "medium" | "high" | null) =>
    request<{
      ai_model_preference: string | null;
      ai_effort_preference?: "low" | "medium" | "high" | null;
    }>("PUT", "/api/v1/users/me/settings/ai-effort", { effort }),
  /** Read-only catalog of models the user can pick from in /settings. */
  listAiModels: () =>
    request<{
      models: Array<{
        id: string;
        label: string;
        description: string;
        recommended?: boolean;
      }>;
    }>("GET", "/api/v1/users/me/settings/ai-models"),
  /** Set or clear (pass `null`) the user's preferred AI model. */
  setAiModel: (modelId: string | null) =>
    request<{ ai_model_preference: string | null }>("PUT", "/api/v1/users/me/settings/ai-model", {
      model_id: modelId,
    }),

  // ── API-tokens + koppelen via de browser ────────────────────────────
  listApiTokens: () => request<{ tokens: ApiTokenDTO[] }>("GET", "/api/v1/users/me/api-tokens"),
  createApiToken: (body: {
    name: string;
    scope: ApiTokenScope;
    workspace_id?: string | null;
    expires_in_days?: number;
    client?: string;
  }) => request<ApiTokenDTO & { token: string }>("POST", "/api/v1/users/me/api-tokens", body),
  revokeApiToken: (id: string) =>
    request<void>("DELETE", `/api/v1/users/me/api-tokens/${encodeURIComponent(id)}`),
  setApiTokenAiPlateauOnly: (id: string, aiPlateauOnly: boolean) =>
    request<ApiTokenDTO>("PATCH", `/api/v1/users/me/api-tokens/${encodeURIComponent(id)}`, {
      ai_plateau_only: aiPlateauOnly,
    }),
  getDeviceApproval: (userCode: string) =>
    request<DeviceApprovalDTO>("GET", `/api/v1/device-approvals/${encodeURIComponent(userCode)}`),
  approveDevice: (
    userCode: string,
    body: { scope: ApiTokenScope; workspace_id: string | null; ai_plateau_only?: boolean },
  ) =>
    request<{ ok: true }>(
      "POST",
      `/api/v1/device-approvals/${encodeURIComponent(userCode)}/approve`,
      body,
    ),
  denyDevice: (userCode: string) =>
    request<{ ok: true }>("POST", `/api/v1/device-approvals/${encodeURIComponent(userCode)}/deny`),

  // ── Admin: workspace mail-config ────────────────────────────────────
  getMailConfig: (wsId: string) =>
    request<{ config: MailConfigPublic | null }>(
      "GET",
      `/api/v1/workspaces/${wsId}/admin/mail-config`,
    ),
  putMailConfig: (
    wsId: string,
    body: {
      host: string;
      port: number;
      username: string;
      /** `null` = laat huidige wachtwoord ongewijzigd; `""` = wis. */
      password: string | null;
      from_address: string;
      from_name: string | null;
      transport: "mailchannels" | "smtp";
    },
  ) =>
    request<{ config: MailConfigPublic | null }>(
      "PUT",
      `/api/v1/workspaces/${wsId}/admin/mail-config`,
      body,
    ),
  deleteMailConfig: (wsId: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/admin/mail-config`),
  testMailConfig: (wsId: string) =>
    request<{ ok: boolean; recipient: string }>(
      "POST",
      `/api/v1/workspaces/${wsId}/admin/mail-config/test`,
    ),

  // ── Admin: per-user AI rate-limit overrides ─────────────────────────
  listAiRateLimits: (wsId: string) =>
    request<{
      overrides: Array<{
        email: string;
        limit_kind: string;
        daily_limit: number;
        updated_at: string;
      }>;
      defaults: { ai_transform: number };
    }>("GET", `/api/v1/workspaces/${wsId}/admin/ai-rate-limits`),
  setAiRateLimit: (
    wsId: string,
    email: string,
    body: { limit_kind: "ai_transform"; daily_limit: number | null },
  ) =>
    request<{ ok: boolean }>(
      "PUT",
      `/api/v1/workspaces/${wsId}/admin/ai-rate-limits/${encodeURIComponent(email)}`,
      body,
    ),

  // ── Admin: garbage-collection (orphan cleanup) ──────────────────────
  runGc: (wsId: string) =>
    request<{
      id: string;
      trigger_kind: "manual";
      status: "success" | "error";
      error?: string;
      duration_ms: number;
      orphan_relations_deleted: number;
      orphan_viewstate_concepts_cleaned: number;
      orphan_viewstate_relations_cleaned: number;
      diagrams_touched: number;
    }>("POST", `/api/v1/workspaces/${wsId}/admin/gc/run`),
  listGcRuns: (wsId: string) =>
    request<{
      runs: Array<{
        id: string;
        started_at: string;
        finished_at: string | null;
        trigger_kind: "cron" | "manual";
        triggered_by: string | null;
        workspace_id: string | null;
        orphan_relations_deleted: number;
        orphan_viewstate_concepts_cleaned: number;
        orphan_viewstate_relations_cleaned: number;
        diagrams_touched: number;
        status: "running" | "success" | "error";
        error: string | null;
      }>;
    }>("GET", `/api/v1/workspaces/${wsId}/admin/gc/runs`),

  // ── Admin: retired-objecten opruimen (meta.Retired) ──────────────────
  previewRetired: (wsId: string, scope: RetiredScopeParam) =>
    request<RetiredPreviewDTO>(
      "GET",
      `/api/v1/workspaces/${wsId}/admin/retired-preview`,
      undefined,
      scope.kind === "workpackage"
        ? { scope: "workpackage", workpackage_id: scope.workpackageId }
        : { scope: "workspace" },
    ),
  cleanupRetired: (wsId: string, scope: RetiredScopeParam) =>
    request<RetiredCleanupResultDTO>("POST", `/api/v1/workspaces/${wsId}/admin/cleanup-retired`, {
      scope: scope.kind,
      ...(scope.kind === "workpackage" ? { workpackage_id: scope.workpackageId } : {}),
      confirm: true,
    }),

  // ── Admin: recovery-audit-log ───────────────────────────────────────
  listRecoveryAudit: (
    wsId: string,
    branchId: string,
    opts?: { action?: RecoveryAuditAction; since?: string; limit?: number },
  ) => {
    const params: Record<string, string | number | undefined> = { branch_id: branchId };
    if (opts?.action) params.action = opts.action;
    if (opts?.since) params.since = opts.since;
    if (opts?.limit) params.limit = opts.limit;
    return request<{ entries: RecoveryAuditEntry[] }>(
      "GET",
      `/api/v1/workspaces/${wsId}/admin/recovery-audit`,
      undefined,
      params,
    );
  },

  // ── Prullenbak (tombstoned items) ────────────────────────────────────
  // Member-toegankelijk sinds 2026-05-21: non-admins zien/herstellen
  // alleen eigen items (server-side filter op updated_by). Route was
  // /admin/trash; nu /trash zodat de admin-only-gate ophoog.
  listTrash: (wsId: string, branchId: string, limit?: number) =>
    request<{ items: TrashItem[] }>("GET", `/api/v1/workspaces/${wsId}/trash`, undefined, {
      branch_id: branchId,
      ...(typeof limit === "number" ? { limit } : {}),
    }),
  restoreTrash: (
    wsId: string,
    branchId: string,
    body: { concept_ids?: string[]; relation_ids?: string[] },
  ) =>
    request<{
      concepts_restored: number;
      relations_restored: number;
      relations_skipped_dangling: string[];
    }>("POST", `/api/v1/workspaces/${wsId}/trash/restore`, body, { branch_id: branchId }),
  purgeTrash: (
    wsId: string,
    branchId: string,
    body: {
      concept_ids?: string[];
      relation_ids?: string[];
      older_than?: string | null;
      confirm?: boolean;
    },
  ) =>
    request<{ concepts_purged: number; relations_purged: number }>(
      "POST",
      `/api/v1/workspaces/${wsId}/admin/trash/purge`,
      body,
      { branch_id: branchId },
    ),

  // ── Workspace export/import ─────────────────────────────────────────
  /**
   * Download een JSON-dump van alle live concepten/relaties/diagrammen
   * in deze workspace+branch. Geeft een `Blob` terug zodat de caller
   * 'm direct als download kan aanbieden via een dynamische `<a>`.
   */
  exportWorkspace: async (
    wsId: string,
    branchId: string,
    opts?: { workpackageId?: string },
  ): Promise<{ blob: Blob; filename: string }> => {
    const url = new URL(`/api/v1/workspaces/${wsId}/export`, window.location.origin);
    url.searchParams.set("branch_id", branchId);
    if (opts?.workpackageId) url.searchParams.set("workpackage", opts.workpackageId);
    const res = await fetch(url.toString(), { credentials: "include" });
    if (!res.ok) {
      throw new ApiError(res.status, null, `Export mislukt: HTTP ${res.status}`);
    }
    const blob = await res.blob();
    // Filename komt uit Content-Disposition; fallback op datum.
    const cd = res.headers.get("content-disposition") ?? "";
    const match = /filename="?([^";]+)"?/.exec(cd);
    const filename = match?.[1] ?? `workspace-export-${new Date().toISOString().slice(0, 10)}.json`;
    return { blob, filename };
  },
  /** Super-admin: nieuwe workspace met een Default- en Base-workpackage. */
  createWorkspace: (body: { name: string; default_language?: "nl" | "en"; admin_email?: string }) =>
    request<{
      id: string;
      name: string;
      default_language: "nl" | "en";
      admin_email: string;
      default_workpackage_id: string;
      base_workpackage_id: string;
    }>("POST", "/api/v1/super-admin/workspaces", body),
  importWorkspace: (wsId: string, branchId: string, payload: unknown) =>
    request<ImportResultDTO>("POST", `/api/v1/workspaces/${wsId}/import`, payload, {
      branch_id: branchId,
    }),

  /** M6.5-W8d — AMEFF (Open Group XML) export. Returns raw XML blob met
   *  Content-Disposition voor download. Schema = ArchiMate 3.0.
   *  Optionele workpackageId-scope (2026-05-26): zelfde patroon als
   *  exportWorkspace, server-side filter via branchId van WP. Geen
   *  param → caller-branch (default WP-context). */
  exportAmeff: async (
    wsId: string,
    branchId: string,
    opts?: { workpackageId?: string },
  ): Promise<{
    blob: Blob;
    filename: string;
    /** Alleen aanwezig wanneer de export concepten heeft moeten overslaan. */
    skipped?: { count: number; typeIds: string[] };
  }> => {
    const url = new URL(`/api/v1/workspaces/${wsId}/export.archimate`, window.location.origin);
    url.searchParams.set("branch_id", branchId);
    if (opts?.workpackageId) url.searchParams.set("workpackage", opts.workpackageId);
    const res = await fetch(url.toString(), { credentials: "include" });
    if (!res.ok) {
      // Probeer de server-error-body te lezen (problem-detail JSON of
      // plain text) zodat de UI iets specifiekers dan "HTTP 500" toont.
      let detail = "";
      try {
        const body = await res.text();
        if (body) {
          try {
            const j = JSON.parse(body) as { detail?: string; title?: string };
            detail = j.detail ?? j.title ?? body;
          } catch {
            detail = body;
          }
        }
      } catch {
        // ignore
      }
      const msg = detail
        ? `AMEFF-export mislukt: ${detail}`
        : `AMEFF-export mislukt: HTTP ${res.status}`;
      throw new ApiError(res.status, null, msg);
    }
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") ?? "";
    const match = /filename="?([^";]+)"?/.exec(cd);
    const filename = match?.[1] ?? `model-${new Date().toISOString().slice(0, 10)}.xml`;
    // Concepten zonder AMEFF-equivalent gaan niet mee. De worker meldt dat
    // via headers omdat de body het XML-bestand zelf is. Vooral relevant voor
    // workspace-subtypes: die bestaan buiten deze workspace niet, dus er valt
    // niets uit te wisselen — en tot 2026-08-11 verdwenen ze zwijgend.
    const skippedCount = Number(res.headers.get("x-ae-skipped-count") ?? "0");
    const skippedTypes = (res.headers.get("x-ae-skipped-types") ?? "")
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    return {
      blob,
      filename,
      ...(skippedCount > 0 ? { skipped: { count: skippedCount, typeIds: skippedTypes } } : {}),
    };
  },

  // ── Branches (per-workspace) ────────────────────────────────────────
  listBranches: (wsId: string, status?: "active" | "merged" | "abandoned") =>
    request<BranchRow[]>(
      "GET",
      `/api/v1/workspaces/${wsId}/branches`,
      undefined,
      status ? { status } : undefined,
    ),
  createBranch: (
    wsId: string,
    body: { name: string; from_branch_id?: string; from_snapshot_id?: string },
  ) => request<BranchRow>("POST", `/api/v1/workspaces/${wsId}/branches`, body),
  patchBranch: (
    wsId: string,
    branchId: string,
    body: { name?: string; status?: "active" | "merged" | "abandoned" },
  ) => request<BranchRow>("PATCH", `/api/v1/workspaces/${wsId}/branches/${branchId}`, body),
  /** Proefrun van samenvoegen in de ouder; schrijft niets. Keuzes per
   *  conflict: `soort:id:pad` of `soort:id` → "source" | "target". */
  previewMergeBranch: (wsId: string, branchId: string, choices: Record<string, MergeSide> = {}) =>
    request<MergePreview>(
      "POST",
      `/api/v1/workspaces/${wsId}/branches/${branchId}/merge`,
      { choices },
      { dry_run: "1" },
    ),
  /** Samenvoegen uitvoeren (onder het slot op het workpackage). */
  mergeBranch: (wsId: string, branchId: string, choices: Record<string, MergeSide> = {}) =>
    request<MergePreview>("POST", `/api/v1/workspaces/${wsId}/branches/${branchId}/merge`, {
      choices,
    }),
  deleteBranch: (wsId: string, branchId: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/branches/${branchId}`),
  /** Welke branches gaan mee als je deze verwijdert (de hele deelboom). */
  previewDeleteBranch: (wsId: string, branchId: string) =>
    request<{
      branches: Array<{
        id: string;
        name: string;
        concepts: number;
        relations: number;
        diagrams: number;
      }>;
    }>("DELETE", `/api/v1/workspaces/${wsId}/branches/${branchId}`, undefined, {
      dry_run: "1",
    }),
  /** Roadmap-lanes: de plateau-keten van de WP van `branchId` (Plateau 1 =
   *  die WP-main), geordend wortel → blad. */
  branchPlateaus: (wsId: string, branchId: string) =>
    request<PlateauBranch[]>("GET", `/api/v1/workspaces/${wsId}/branches/plateaus`, undefined, {
      branch_id: branchId,
    }),
  /** Maak een nieuw Plateau (concept-op-main + gechainde branch + link). */
  createPlateau: (
    wsId: string,
    body: { name: string; nameNl?: string; nameEn?: string; parent_branch_id?: string },
  ) =>
    request<{ branch: BranchRow | null; plateau: { id: string } }>(
      "POST",
      `/api/v1/workspaces/${wsId}/branches/plateau`,
      body,
    ),

  /** Ensure een Plateau-concept voor een branch die er nog geen heeft (typisch
   *  main → "Huidige situatie"). Idempotent. Returnt de plateau-concept-id. */
  ensurePlateauConcept: (
    wsId: string,
    branchId: string,
    body?: { name?: string; nameEn?: string },
  ) =>
    request<{ plateauConceptId: string }>(
      "POST",
      `/api/v1/workspaces/${wsId}/branches/${branchId}/ensure-plateau-concept`,
      body ?? {},
    ),

  // ── Snapshots (workspace-state checkpoints) ─────────────────────────
  listSnapshots: (wsId: string, branchId: string, allBranches = false) =>
    request<{ items: SnapshotMetadata[] }>(
      "GET",
      `/api/v1/workspaces/${wsId}/snapshots`,
      undefined,
      { branch_id: branchId, all: allBranches ? "true" : undefined },
    ),
  createSnapshot: (
    wsId: string,
    branchId: string,
    body: { label?: string; message?: string | null },
  ) =>
    request<SnapshotMetadata & { reused?: boolean }>(
      "POST",
      `/api/v1/workspaces/${wsId}/snapshots`,
      body,
      {
        branch_id: branchId,
      },
    ),
  getSnapshotMeta: (wsId: string, branchId: string, snapshotId: string) =>
    request<SnapshotMetadata & { content_url: string }>(
      "GET",
      `/api/v1/workspaces/${wsId}/snapshots/${snapshotId}`,
      undefined,
      { branch_id: branchId },
    ),
  restoreSnapshot: (wsId: string, branchId: string, snapshotId: string) =>
    request<{
      restored_from: SnapshotMetadata;
      safety_snapshot: SnapshotMetadata;
      summary: {
        concepts_inserted: number;
        concepts_updated: number;
        /** Soft-tombstones op feature-branches; 0 op main. */
        concepts_tombstoned: number;
        /** Hard-deletes op main-branches; 0 op feature-branches. Veld toegevoegd 2026-05-24. */
        concepts_deleted?: number;
        relations_inserted: number;
        relations_updated: number;
        relations_tombstoned: number;
        relations_deleted?: number;
        diagrams_replaced: number;
        diagrams_removed: number;
      };
    }>("POST", `/api/v1/workspaces/${wsId}/snapshots/${snapshotId}/restore`, undefined, {
      branch_id: branchId,
    }),
  deleteSnapshot: (wsId: string, branchId: string, snapshotId: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/snapshots/${snapshotId}`, undefined, {
      branch_id: branchId,
    }),

  // ── "Vraag Claude" — mail het resultaat naar jezelf ─────────────────
  emailAiResult: (body: {
    workspace_id?: string | null;
    subject: string;
    intro_html?: string;
    content_type: "image/svg+xml" | "text/plain";
    content: string;
    filename?: string;
  }) => request<{ ok: boolean; recipient: string }>("POST", "/api/v1/ai/email-result", body),

  // ── M6 Explorer — workpackages + branches + tree ─────────────────
  listWorkpackages: (wsId: string, includeClosed = false) =>
    request<{ workpackages: WorkPackageDTO[] }>(
      "GET",
      `/api/v1/workspaces/${wsId}/workpackages`,
      undefined,
      includeClosed ? { include_closed: "true" } : undefined,
    ),
  createWorkpackage: (wsId: string, body: { name: string; description?: string | null }) =>
    request<WorkPackageDTO>("POST", `/api/v1/workspaces/${wsId}/workpackages`, body),
  patchWorkpackage: (
    wsId: string,
    wpId: string,
    body: {
      name?: string;
      description?: string | null;
      status?: "open" | "closed";
      ai_write?: AiWriteMode;
    },
  ) => request<WorkPackageDTO>("PATCH", `/api/v1/workspaces/${wsId}/workpackages/${wpId}`, body),
  getWorkpackage: (wsId: string, wpId: string) =>
    request<WorkPackageDTO>("GET", `/api/v1/workspaces/${wsId}/workpackages/${wpId}`),
  deleteWorkpackage: (wsId: string, wpId: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/workpackages/${wpId}`),
  // ── M6.5 Folders (admin-only, in de Base-WP) ────────────────────
  listFolders: (wsId: string) =>
    request<{ folders: FolderDTO[]; baseWorkpackageId: string }>(
      "GET",
      `/api/v1/workspaces/${wsId}/folders`,
    ),
  createFolder: (
    wsId: string,
    body: { name: string; description?: string | null; type_group?: FolderTypeGroup },
  ) => request<FolderDTO>("POST", `/api/v1/workspaces/${wsId}/folders`, body),
  patchFolder: (
    wsId: string,
    folderId: string,
    body: { name?: string; description?: string | null },
  ) => request<FolderDTO>("PATCH", `/api/v1/workspaces/${wsId}/folders/${folderId}`, body),
  deleteFolder: (wsId: string, folderId: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/folders/${folderId}`),
  /** M6.5-W6 — verhuis concepten naar een folder. Returnt
   *  `{moved, skipped}` met partial-success per concept-id. */
  moveConceptsToFolder: (wsId: string, folderId: string, conceptIds: string[]) =>
    request<{ moved: string[]; skipped: Array<{ id: string; reason: string }> }>(
      "POST",
      `/api/v1/workspaces/${wsId}/folders/${folderId}/move-in`,
      { conceptIds },
    ),
  /** M6.5-W6 — verplaats folder-concepten uit een folder naar de WP
   *  die write-recht op de folder heeft (target wordt server-side
   *  bepaald uit `workpackage_folder_access`). Admin-only. 422 wanneer
   *  geen WP write-recht heeft. */
  moveConceptsOutOfFolder: (wsId: string, folderId: string, conceptIds: string[]) =>
    request<{ moved: string[]; skipped: Array<{ id: string; reason: string }> }>(
      "POST",
      `/api/v1/workspaces/${wsId}/folders/${folderId}/move-out`,
      { conceptIds },
    ),
  /** M6.5-W6 — lookup van write-houder voor een folder. Admin-only. */
  getFolderWriteHolder: (wsId: string, folderId: string) =>
    request<{ workpackageId: string | null; workpackageName: string | null }>(
      "GET",
      `/api/v1/workspaces/${wsId}/folders/${folderId}/write-holder`,
    ),
  /** M6.5-W8b — folder-scope JSON-export. Admin-only. Returnt
   *  `{blob, filename}` net als workspace-export. */
  exportFolder: async (
    wsId: string,
    folderId: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const url = new URL(
      `/api/v1/workspaces/${wsId}/folders/${folderId}/export`,
      window.location.origin,
    );
    const res = await fetch(url.toString(), { credentials: "include" });
    if (!res.ok) {
      throw new ApiError(res.status, null, `Folder-export mislukt: HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") ?? "";
    const match = /filename="?([^";]+)"?/.exec(cd);
    const filename = match?.[1] ?? `folder-export-${new Date().toISOString().slice(0, 10)}.json`;
    return { blob, filename };
  },
  /** M6.5-W8b — folder-scope JSON-import. Admin-only. UPSERT-by-UUID:
   *  bestaande rijen met dezelfde id worden bijgewerkt; nieuwe ids worden
   *  geïnserteerd met behoud van originele UUID. */
  importFolder: (wsId: string, folderId: string, payload: unknown) =>
    request<{
      concepts_inserted: number;
      concepts_updated?: number;
      /** Objecten die een nieuw id kregen omdat het oude in een andere workspace bestaat. */
      ids_reassigned?: number;
      relations_inserted: number;
      relations_skipped: Array<{ id: string; reason: string }>;
      error?: string;
    }>("POST", `/api/v1/workspaces/${wsId}/folders/${folderId}/import`, payload),
  // ── M6.5-W4 Folder-subscriptions per workpackage (admin-only) ────
  listFolderAccess: (wsId: string, wpId: string) =>
    request<{ access: FolderAccessDTO[]; writeHolders: Record<string, string> }>(
      "GET",
      `/api/v1/workspaces/${wsId}/workpackages/${wpId}/folder-access`,
    ),
  setFolderAccess: (wsId: string, wpId: string, folderId: string, mode: FolderAccessMode) =>
    request<FolderAccessDTO>(
      "PUT",
      `/api/v1/workspaces/${wsId}/workpackages/${wpId}/folder-access/${folderId}`,
      { mode },
    ),
  revokeFolderAccess: (wsId: string, wpId: string, folderId: string) =>
    request<void>(
      "DELETE",
      `/api/v1/workspaces/${wsId}/workpackages/${wpId}/folder-access/${folderId}`,
    ),
  // ── M6.5-W7 Cross-workpackage copy (admin) ──────────────────────
  copyAcrossWorkpackages: (
    wsId: string,
    targetWpId: string,
    body: {
      sourceWorkpackageId: string;
      conceptIds: string[];
      diagramIds: string[];
      mode: "match" | "always-new";
      dryRun?: boolean;
    },
  ) =>
    request<{
      concepts: { reused: number; created: number; skipped: number };
      relations: { reused: number; created: number; skipped: number };
      diagrams: { reused: number; created: number; skipped: number };
      mapping: Record<string, string>;
      details: Array<{
        kind: "concept" | "relation" | "diagram";
        sourceId: string;
        action: "reused" | "created" | "skipped";
        name: { nl?: string; en?: string };
        typeId: string;
        reason?: string;
      }>;
    }>("POST", `/api/v1/workspaces/${wsId}/workpackages/${targetWpId}/copy-from`, body),
  listWorkpackageBranches: (wsId: string, wpId: string) =>
    request<{ branches: BranchSummary[] }>(
      "GET",
      `/api/v1/workspaces/${wsId}/workpackages/${wpId}/branches`,
    ),
  createWorkpackageBranch: (
    wsId: string,
    wpId: string,
    body: { name: string; parentBranchId?: string; visibleToReadTeams?: boolean },
  ) =>
    request<{ id: string; name: string; workpackageId: string }>(
      "POST",
      `/api/v1/workspaces/${wsId}/workpackages/${wpId}/branches`,
      body,
    ),
  getExplorerTree: (wsId: string, wpId: string, bId: string) =>
    request<ExplorerTreeDTO>(
      "GET",
      `/api/v1/workspaces/${wsId}/workpackages/${wpId}/branches/${bId}/tree`,
    ),
  getObjectDetail: (wsId: string, conceptId: string, branchId?: string) =>
    request<ObjectDetailDTO>(
      "GET",
      `/api/v1/workspaces/${wsId}/objects/${conceptId}`,
      undefined,
      branchId ? { branchId } : undefined,
    ),
  getObjectDiagrams: (wsId: string, conceptId: string, branchId?: string) =>
    request<{ diagrams: DiagramOccurrenceDTO[] }>(
      "GET",
      `/api/v1/workspaces/${wsId}/objects/${conceptId}/diagrams`,
      undefined,
      branchId ? { branchId } : undefined,
    ),
  getDiagramDetail: (wsId: string, diagramId: string) =>
    request<DiagramDetailDTO>("GET", `/api/v1/workspaces/${wsId}/diagrams-detail/${diagramId}`),
  // ── Teams ─────────────────────────────────────────────────────────
  listTeams: (wsId: string) =>
    request<{ teams: TeamDTO[] }>("GET", `/api/v1/workspaces/${wsId}/teams`),
  createTeam: (
    wsId: string,
    body: { name: string; description?: string | null; foundingMember: string },
  ) => request<TeamDTO>("POST", `/api/v1/workspaces/${wsId}/teams`, body),
  deleteTeam: (wsId: string, teamId: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/teams/${teamId}`),
  getTeam: (wsId: string, teamId: string) =>
    request<
      TeamDTO & {
        members: Array<{ teamId: string; userEmail: string; addedBy?: string; addedAt: string }>;
      }
    >("GET", `/api/v1/workspaces/${wsId}/teams/${teamId}`),
  addTeamMember: (wsId: string, teamId: string, email: string) =>
    request<{ ok: boolean; email: string }>(
      "POST",
      `/api/v1/workspaces/${wsId}/teams/${teamId}/members`,
      { email },
    ),
  removeTeamMember: (wsId: string, teamId: string, email: string) =>
    request<void>(
      "DELETE",
      `/api/v1/workspaces/${wsId}/teams/${teamId}/members/${encodeURIComponent(email)}`,
    ),
  // ── Workpackage-permissions (admin) ──────────────────────────────
  listWorkpackagePermissions: (wsId: string) =>
    request<{
      permissions: Array<{
        teamId: string;
        workpackageId: string;
        permission: "read" | "write";
        grantedBy: string;
        grantedAt: string;
      }>;
    }>("GET", `/api/v1/workspaces/${wsId}/workpackage-permissions`),
  setWorkpackagePermission: (
    wsId: string,
    teamId: string,
    wpId: string,
    permission: "read" | "write",
  ) =>
    request<{ teamId: string; workpackageId: string; permission: "read" | "write" }>(
      "PUT",
      `/api/v1/workspaces/${wsId}/workpackage-permissions/${teamId}/${wpId}`,
      { permission },
    ),
  revokeWorkpackagePermission: (wsId: string, teamId: string, wpId: string) =>
    request<void>("DELETE", `/api/v1/workspaces/${wsId}/workpackage-permissions/${teamId}/${wpId}`),
  /** POST /api/v1/auth/logout — wist sessie + email-cookies. Daarna
   *  moet de UI een full reload doen zodat AuthGate naar /login stuurt. */
  logout: () => request<{ ok: true }>("POST", "/api/v1/auth/logout"),
  /**
   * Workpackage-scoped snapshot create (variant op snapshots-endpoint
   * die alleen objecten in deze workpackage / folder / branch pakt).
   */
  /** Een snapshot van een workpackage, branch of folder verwijderen (met het
   *  bestand). Workspace-snapshots gaan via `deleteSnapshot` (admin). */
  deleteWorkpackageSnapshot: (wsId: string, branchId: string, snapshotId: string) =>
    request<void>(
      "DELETE",
      `/api/v1/workspaces/${wsId}/snapshots-workpackage/${snapshotId}`,
      undefined,
      { branch_id: branchId },
    ),
  createWorkpackageSnapshot: (
    wsId: string,
    branchId: string,
    body: { folder_id?: string; message?: string },
  ) =>
    request<{
      snapshot: SnapshotMetadata;
      /** `true` als er niets veranderd was: dit is dan de bestaande snapshot. */
      reused?: boolean;
      scope: {
        kind: "branch" | "workpackage" | "folder";
        workpackage_id: string;
        workpackage_name: string;
        branch_id: string;
        branch_name: string;
        folder_id?: string;
        folder_name?: string;
      };
    }>("POST", `/api/v1/workspaces/${wsId}/snapshots-workpackage/workpackage`, body, {
      branch_id: branchId,
    }),
  listWorkpackageSnapshots: (wsId: string, branchId: string, folderId?: string) =>
    request<{
      items: SnapshotMetadata[];
      scope: {
        kind: "branch" | "workpackage" | "folder";
        workpackage_id: string;
        workpackage_name: string;
        branch_id: string;
        branch_name: string;
        folder_id?: string;
        folder_name?: string;
      };
    }>("GET", `/api/v1/workspaces/${wsId}/snapshots-workpackage/workpackage`, undefined, {
      branch_id: branchId,
      ...(folderId ? { folder_id: folderId } : {}),
    }),
  restoreWorkpackageSnapshot: (wsId: string, branchId: string, snapId: string) =>
    request<{
      restored: {
        concepts: number;
        relations: number;
        diagrams: number;
        viewStates?: number;
      };
      scope: unknown;
    }>(
      "POST",
      `/api/v1/workspaces/${wsId}/snapshots-workpackage/${snapId}/restore-workpackage`,
      undefined,
      { branch_id: branchId },
    ),

  /** M6.5-W8c — AMEFF (Open Group ArchiMate XML) import. Raw XML body. */
  importAmeff: async (wsId: string, branchId: string, xml: string) => {
    const url = new URL(`/api/v1/workspaces/${wsId}/import.archimate`, window.location.origin);
    url.searchParams.set("branch_id", branchId);
    const res = await fetch(url.toString(), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/xml" },
      body: xml,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      throw new ApiError(res.status, null, detail || `AMEFF-import mislukt: HTTP ${res.status}`);
    }
    return (await res.json()) as {
      concepts_inserted: number;
      relations_inserted: number;
      diagrams_inserted: number;
      view_states_written: number;
      concepts_skipped: Array<{ id: string; reason: string }>;
      relations_skipped: Array<{ id: string; reason: string }>;
      diagrams_skipped: Array<{ id: string; reason: string }>;
      /** Ingevoerd, maar een mens moet ernaar kijken — zie de import-handler. */
      relations_needing_attention?: Array<{
        id: string;
        kind: "path_composition" | "merged_service_realization" | "invalid_relation";
        note: string;
      }>;
      unknown_element_types: string[];
      unknown_relationship_types: string[];
      labels: Array<{
        concept_id: string;
        name: string;
        type_id: string;
        stereotypes: string[];
        source: "heuristic" | "unclassified";
      }>;
      diagrams: Array<{
        diagram_id: string;
        name: string;
        view_kind: string;
        source: "heuristic";
      }>;
      ambiguous: Array<{
        concept_id: string;
        name: string;
        type_id: string;
        stereotype: string;
        legacy_type_id: string;
        candidates: string[];
      }>;
    };
  },

  /**
   * Specialisatieprofielen die binnen deze branch gelden — pack-geleverd en
   * zelf gedefinieerd. De registry legt ze over de pack-definities heen.
   */
  listProfiles: (workspaceId: string, branchId: string) =>
    request<{
      profiles: Array<{
        name: string;
        label?: BilingualText;
        description?: BilingualText;
        applies_to: string[];
        attributes?: unknown[];
        icon_id?: string | null;
        notation?: { fill?: string; stroke?: string; text_color?: string } | null;
        pack_id: string | null;
        workpackage_id: string;
        editable: boolean;
      }>;
      unreadable: number;
    }>("GET", `/api/v1/workspaces/${workspaceId}/profiles`, undefined, { branch_id: branchId }),

  createProfile: (
    workspaceId: string,
    branchId: string,
    body: {
      name: string;
      label?: BilingualText;
      applies_to: string[];
      attributes?: unknown[];
      icon_id?: string;
      notation?: { fill?: string; stroke?: string; text_color?: string };
    },
  ) =>
    request<{ name: string }>("POST", `/api/v1/workspaces/${workspaceId}/profiles`, body, {
      branch_id: branchId,
    }),

  updateProfile: (
    workspaceId: string,
    branchId: string,
    name: string,
    body: {
      /** Hernoemen. De server cascadeert alle verwijzingen mee. */
      new_name?: string;
      label?: BilingualText;
      applies_to?: string[];
      attributes?: unknown[];
      icon_id?: string;
      notation?: { fill?: string; stroke?: string; text_color?: string };
    },
  ) =>
    request<{
      name: string;
      renamed_from?: string;
      references?: { concepts: number; relations: number; diagrams: number };
      /** Diagrammen die de oude naam nog in hun viewState noemen — niet
       *  meegecascadeerd, wel gemeld. */
      view_state_mentions?: number;
    }>("PATCH", `/api/v1/workspaces/${workspaceId}/profiles/${encodeURIComponent(name)}`, body, {
      branch_id: branchId,
    }),

  deleteProfile: (workspaceId: string, branchId: string, name: string) =>
    request<{ deleted: string }>(
      "DELETE",
      `/api/v1/workspaces/${workspaceId}/profiles/${encodeURIComponent(name)}`,
      undefined,
      { branch_id: branchId },
    ),

  // ── Super-admin cross-workspace switching (Settings → Wissel workspace) ──
  superAdminStatus: (workspaceId?: string) =>
    request<{ masterPasswordSet: boolean; workspaceAccessPasswordSet: boolean | null }>(
      "GET",
      "/api/v1/super-admin/status",
      undefined,
      workspaceId ? { workspace_id: workspaceId } : undefined,
    ),
  listSuperAdmins: () => request<{ admins: SuperAdminRef[] }>("GET", "/api/v1/super-admin/admins"),
  addSuperAdmin: (email: string) =>
    request<{ admins: SuperAdminRef[] }>("POST", "/api/v1/super-admin/admins", { email }),
  removeSuperAdmin: (email: string) =>
    request<{ admins: SuperAdminRef[] }>(
      "DELETE",
      `/api/v1/super-admin/admins/${encodeURIComponent(email)}`,
    ),
  setMasterPassword: (newPassword: string, currentPassword?: string) =>
    request<{ ok: boolean }>("PUT", "/api/v1/super-admin/master-password", {
      newPassword,
      ...(currentPassword ? { currentPassword } : {}),
    }),
  clearMasterPassword: (currentPassword: string) =>
    request<{ ok: boolean }>("DELETE", "/api/v1/super-admin/master-password", {
      currentPassword,
    }),
  setWorkspaceAccessPassword: (wsId: string, newPassword: string, currentPassword?: string) =>
    request<{ ok: boolean }>("PUT", `/api/v1/super-admin/workspaces/${wsId}/access-password`, {
      newPassword,
      ...(currentPassword ? { currentPassword } : {}),
    }),
  // ── Micro-sloten (gelijktijdigheid, zie docs/architecture/gelijktijdigheid.md)
  //
  // De houder is het e-mailadres uit de sessie; de client stuurt 'm bewust
  // niet mee. Alles faalt zacht aan de serverkant: hapert de locking-laag,
  // dan krijgt iedereen z'n slot en gaat het werk door zonder bescherming.
  acquireObjectLocks: (wsId: string, branchId: string, ids: string[]) =>
    request<{
      granted: string[];
      denied: Array<{
        id: string;
        holder: string;
        kind: "micro" | "full";
        reason: "object" | "scope";
      }>;
    }>("POST", `/api/v1/workspaces/${wsId}/locks/acquire`, { ids }, { branch_id: branchId }),

  refreshObjectLocks: (wsId: string, branchId: string, ids: string[]) =>
    request<{ refreshed: string[] }>(
      "POST",
      `/api/v1/workspaces/${wsId}/locks/refresh`,
      { ids },
      {
        branch_id: branchId,
      },
    ),

  releaseObjectLocks: (wsId: string, branchId: string, ids: string[]) =>
    request<{ released: string[] }>(
      "POST",
      `/api/v1/workspaces/${wsId}/locks/release`,
      { ids },
      {
        branch_id: branchId,
      },
    ),

  /** Droge run van een JSON-import: dezelfde beslissingen, geen schrijfactie. */
  dryRunImport: (wsId: string, branchId: string, payload: unknown) =>
    request<OperationPreview>("POST", `/api/v1/workspaces/${wsId}/import`, payload, {
      branch_id: branchId,
      dry_run: "1",
    }),

  /** Droge run van een AMEFF-import: alleen ontleden, raakt de database niet. */
  dryRunImportAmeff: async (wsId: string, branchId: string, xml: string) => {
    const url = new URL(`/api/v1/workspaces/${wsId}/import.archimate`, window.location.origin);
    url.searchParams.set("branch_id", branchId);
    url.searchParams.set("dry_run", "1");
    const res = await fetch(url.toString(), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/xml" },
      body: xml,
    });
    if (!res.ok) {
      const tekst = await res.text();
      throw new ApiError(res.status, null, tekst || `HTTP ${res.status}`);
    }
    return (await res.json()) as OperationPreview & { always_inserts?: boolean };
  },

  /** Droge run van een workspace-brede restore. */
  dryRunRestore: (wsId: string, branchId: string, snapshotId: string) =>
    request<
      OperationPreview & {
        /** Bij een branch-snapshot: wat de restore met de deelboom doet. */
        branch_check?: {
          ok: boolean;
          structure: {
            remove: Array<{ id: string; name: string }>;
            recreate: Array<{ id: string; name: string }>;
          };
        };
      }
    >(
      "POST",
      `/api/v1/workspaces/${wsId}/snapshots/${snapshotId}/restore`,
      {},
      { branch_id: branchId, dry_run: "1" },
    ),

  /** Droge run van een restore binnen één workpackage. */
  dryRunRestoreWorkpackage: (wsId: string, branchId: string, snapshotId: string) =>
    request<
      OperationPreview & {
        /** Bij een branch-snapshot: wat de restore met de deelboom doet. */
        branch_check?: {
          ok: boolean;
          structure: {
            remove: Array<{ id: string; name: string }>;
            recreate: Array<{ id: string; name: string }>;
          };
          ancestors: Array<{ branch_id: string; name: string; relation: string }>;
        };
      }
    >(
      "POST",
      `/api/v1/workspaces/${wsId}/snapshots-workpackage/${snapshotId}/restore-workpackage`,
      {},
      { branch_id: branchId, dry_run: "1" },
    ),

  // ── Aangekondigde operaties (import/restore) ──────────────────────
  // Zie `docs/architecture/gelijktijdigheid.md` §7.

  listActiveOperations: (wsId: string, branchId: string) =>
    request<{ operations: PendingOperationView[] }>(
      "GET",
      `/api/v1/workspaces/${wsId}/operations/active`,
      undefined,
      { branch_id: branchId },
    ),

  announceOperation: (
    wsId: string,
    branchId: string,
    body: {
      kind: "import" | "restore" | "merge";
      label: string;
      /** Weglaten laat de server de scope afleiden uit de branch of de snapshot. */
      workpackage_ids?: string[];
      snapshot_id?: string;
      impact?: Record<string, number>;
      preview?: OperationPreview;
      countdown_minutes?: number;
    },
  ) =>
    request<{ announced: boolean; reason?: string; operation?: PendingOperationView }>(
      "POST",
      `/api/v1/workspaces/${wsId}/operations`,
      body,
      { branch_id: branchId },
    ),

  respondToOperation: (
    wsId: string,
    branchId: string,
    id: string,
    action: "ack" | "extend" | "start-now" | "cancel",
  ) =>
    request<PendingOperationView>(
      "POST",
      `/api/v1/workspaces/${wsId}/operations/${id}/${action}`,
      {},
      { branch_id: branchId },
    ),

  setOperationState: (wsId: string, branchId: string, id: string, state: "running" | "done") =>
    request<PendingOperationView>(
      "POST",
      `/api/v1/workspaces/${wsId}/operations/${id}/state`,
      { state },
      { branch_id: branchId },
    ),

  renameWorkspace: (wsId: string, name: string) =>
    request<{ id: string; name: string }>("PATCH", `/api/v1/super-admin/workspaces/${wsId}`, {
      name,
    }),
  clearWorkspaceAccessPassword: (wsId: string, currentPassword: string) =>
    request<{ ok: boolean }>("DELETE", `/api/v1/super-admin/workspaces/${wsId}/access-password`, {
      currentPassword,
    }),
  listSuperAdminWorkspaces: (masterPassword?: string) =>
    request<{ workspaces: SuperAdminWorkspaceRef[] }>(
      "POST",
      "/api/v1/super-admin/workspaces/list",
      masterPassword ? { masterPassword } : {},
    ),
  switchSuperAdminWorkspace: (
    targetWorkspaceId: string,
    opts: { masterPassword?: string; workspaceAccessPassword?: string },
  ) =>
    request<{ workspaceId: string; workspaceName: string }>("POST", "/api/v1/super-admin/switch", {
      targetWorkspaceId,
      ...opts,
    }),
  exitSuperAdminOverride: () =>
    request<{ ok: boolean }>("POST", "/api/v1/super-admin/exit-override"),

  // ── AI-aanmelding van de installatie (super-admin, §4.2) ─────────────
  getAiAuth: () => request<AiAuthStatusDTO>("GET", "/api/v1/super-admin/ai-auth"),
  putAiAuth: (body: {
    wif?: {
      organization_id: string;
      service_account_id: string;
      federation_rule_id: string;
      anthropic_workspace_id?: string | null;
    } | null;
    api_key?: string | null;
    /** Anthropic-workspace bij de sleutel (`wrkspc_…`); `null` = geen. */
    api_key_workspace_id?: string | null;
  }) => request<AiAuthStatusDTO>("PUT", "/api/v1/super-admin/ai-auth", body),
  deleteAiAuth: () => request<void>("DELETE", "/api/v1/super-admin/ai-auth"),
  testAiAuth: () =>
    request<{
      ok: boolean;
      mode: "wif" | "api_key" | "dev_key" | null;
      status?: number;
      detail?: string;
    }>("POST", "/api/v1/super-admin/ai-auth/test"),

  // ── Globale mailinstelling (ook voor de inlogcodes) ──────────────────
  getGlobalMailConfig: () =>
    request<{ config: MailConfigPublic | null }>("GET", "/api/v1/super-admin/mail-config"),
  putGlobalMailConfig: (body: MailConfigPutBody) =>
    request<{ config: MailConfigPublic | null }>("PUT", "/api/v1/super-admin/mail-config", body),
  deleteGlobalMailConfig: () => request<void>("DELETE", "/api/v1/super-admin/mail-config"),
  testGlobalMailConfig: () =>
    request<{ ok: boolean; recipient: string }>("POST", "/api/v1/super-admin/mail-config/test"),

  // ── Tab 6: cron jobs ──────────────────────────────────────────────────
  listLegacySnapshots: () =>
    request<LegacySnapshotsDTO>("GET", "/api/v1/super-admin/legacy-snapshots"),
  purgeLegacySnapshots: (expectedSnapshots: number, expectedOrphanFiles: number) =>
    request<{ snapshots_deleted: number; files_deleted: number }>(
      "POST",
      "/api/v1/super-admin/legacy-snapshots/purge",
      { expected_snapshots: expectedSnapshots, expected_orphan_files: expectedOrphanFiles },
    ),
  listCronJobs: () => request<{ jobs: CronJobDTO[] }>("GET", "/api/v1/super-admin/cron-jobs"),
  updateCronJob: (
    name: string,
    patch: { enabled?: boolean; hourUtc?: number; frequencyDays?: number },
  ) => request<CronJobDTO>("PUT", `/api/v1/super-admin/cron-jobs/${name}`, patch),
  runCronJobNow: (name: string) =>
    request<CronJobRunResultDTO>("POST", `/api/v1/super-admin/cron-jobs/${name}/run`),
  listCronJobRuns: (name: string) =>
    request<{ runs: CronJobRunDTO[] }>("GET", `/api/v1/super-admin/cron-jobs/${name}/runs`),

  /** Zoek-en-kopieer tegen de master-workspace's MITRE CWE-bibliotheek —
   *  zie `CweLibraryPicker.tsx`. */
  searchMitreCwe: (q: string) =>
    request<{ configured: boolean; results: CweSearchResultDTO[] }>(
      "GET",
      "/api/v1/mitre-cwe/search",
      undefined,
      { q },
    ),
  /** Live NVD-lookup van CVE's voor een CWE-ID — zie `CweLibraryPicker.tsx`. */
  searchNvdCve: (cweId: string) =>
    request<{ results: CveSearchResultDTO[] }>("GET", "/api/v1/mitre-cwe/cve-search", undefined, {
      cweId,
    }),
};

export interface CweSearchResultDTO {
  id: string;
  cweId: string;
  name: BilingualText;
  description: BilingualText;
  abstraction: string;
  structure: string;
  status: string;
  impacts: string;
  likelihoodOfExploit: string;
  relatedCwes: string;
  references: string;
  alternateTerms: string;
}

export interface CveSearchResultDTO {
  cveId: string;
  description: string;
  cvssScore: number | null;
  cvssVector: string | null;
  cvssSeverity: string | null;
  published: string | null;
}

/** Snapshots van formaat 1 (van vóór migratie 0054) die opgeruimd kunnen
 *  worden, plus het aantal weesbestanden in R2. */
export interface LegacySnapshotsDTO {
  snapshots: Array<{
    id: string;
    workspace_id: string;
    label: string;
    scope_kind: string | null;
    created_at: string;
  }>;
  orphan_files: number;
}

export interface CronJobDTO {
  name: string;
  enabled: boolean;
  hourUtc: number;
  frequencyDays: number;
  lastRunAt: string | null;
  updatedAt: string;
  updatedBy: string | null;
  due: boolean;
}

export interface CronJobRunDTO {
  id: string;
  job_name: string;
  trigger_kind: "cron" | "manual";
  triggered_by: string | null;
  started_at: string;
  finished_at: string | null;
  status: "running" | "success" | "error";
  summary_json: string | null;
  error: string | null;
  /** Alleen-lees, afgeleid — zie `db/cronJobs.ts`'s `isPossiblyStuck`.
   *  Puur presentatie ("mogelijk vastgelopen"), geen eigen DB-kolom. */
  possiblyStuck: boolean;
}

export interface CronJobRunResultDTO {
  id: string;
  jobName: string;
  /** "running" = async gedispatcht (mitre_cwe_sync via GitHub Actions),
   *  nog niet afgerond — zie CLAUDE.md's "MITRE CWE-sync — GitHub Actions
   *  split"-caveat. */
  status: "success" | "error" | "running";
  summary?: unknown;
  error?: string;
}

export interface SuperAdminRef {
  email: string;
  addedAt: string;
  addedBy: string;
}

export interface SuperAdminWorkspaceRef {
  id: string;
  name: string;
  hasAccessPassword: boolean;
}

/** Resultaat van een workspace-import. */
export interface ImportResultDTO {
  concepts_inserted: number;
  concepts_updated: number;
  /** M6.5-W8a — Reference-Objects die via folder-naam + concept-naam +
   *  type matchten met een bestaand object in target; hergebruikt. */
  concepts_reused: number;
  relations_inserted: number;
  relations_updated: number;
  diagrams_inserted: number;
  diagrams_updated: number;
  /** Totaal aantal viewState-entries dat de import naar D1 schreef
   *  (sum over geïmporteerde/geüpdate diagrammen). */
  view_states_written: number;
  custom_view_kinds_inserted: number;
  custom_view_kinds_updated: number;
  /** Aantal folders dat door deze import is auto-aangemaakt in Base. */
  folders_created: number;
  relations_skipped: number;
  imported_with_suffix: Array<{
    kind: "concept" | "relation" | "diagram" | "custom_view_kind";
    id: string;
    original_name: string;
    type_id: string | null;
    /** M6.5-W8a:
     *  - `name_collision` (default) = naam matchte een bestaand object →
     *    `[imported]`-suffix toegevoegd aan de naam
     *  - `duplicate_uuid_resolved` = UUID was elders in workspace al in
     *    gebruik → verse UUID toegekend, naam ongewijzigd */
    reason?: "name_collision" | "duplicate_uuid_resolved";
  }>;
  /** Batches die mislukten (ontbreekt bij een server van vóór 2026-10). */
  failed?: Array<{ label: string; statements: number; error: string }>;
  safety_snapshot_id: string | null;
  safety_snapshot_label: string | null;
}

/** Action-type uit `recovery_audit`. */
export type RecoveryAuditAction =
  | "trash_restore"
  | "trash_purge"
  | "snapshot_create"
  | "snapshot_restore"
  | "snapshot_delete";

/** Wire shape voor één rij uit `recovery_audit`. */
export interface RecoveryAuditEntry {
  id: string;
  workspace_id: string;
  action: RecoveryAuditAction;
  performed_at: string;
  performed_by: string;
  details: Record<string, unknown>;
}

/** Wire shape voor één rij in `snapshots` (D1) + R2-blob-grootte. */
export interface SnapshotMetadata {
  id: string;
  branch_id: string;
  workspace_id: string;
  label: string;
  message: string | null;
  created_at: string;
  created_by: string;
  counts: { concepts: number; relations: number; diagrams: number; viewStates?: number };
  byte_size: number;
  scope_kind?: "workspace" | "workpackage_main" | "workpackage_branch" | "folder";
  /** Kan deze snapshot terug? `later`: eerst `first` terugzetten; `never`: opruimen mag. */
  restorable?: {
    status: "yes" | "later" | "never";
    reason?: string;
    first?: Array<{ id: string; label: string }>;
  };
}

/** Wire shape voor één rij uit `concept_versions` / `relation_versions`. */
export interface VersionEntry<TSnapshot> {
  version_number: number;
  branch_id: string;
  change_reason: string | null;
  created_at: string;
  created_by: string;
  /** Volledige domain-shape op deze versie. */
  snapshot: TSnapshot;
}

/** Live-diagram waarop een tombstoned item stond vóór verwijdering.
 *  Alleen niet-tombstoned diagrams worden door de server geretourneerd. */
export interface TrashDiagramRef {
  id: string;
  name: BilingualText;
}

/** Wire shape voor één item in /trash. */
export type TrashItem =
  | {
      kind: "concept";
      id: string;
      type_id: string;
      name: BilingualText;
      tombstoned_at: string;
      tombstoned_by: string;
      branch_id: string;
      version: number;
      /** Multi-delete-groepering — gedeelde UUID per delete-actie of
       *  null voor pre-migration / single-delete. */
      delete_action_id: string | null;
      placed_on_diagrams: TrashDiagramRef[];
    }
  | {
      kind: "relation";
      id: string;
      type_id: string;
      source_concept_id: string;
      target_concept_id: string;
      label: BilingualText;
      tombstoned_at: string;
      tombstoned_by: string;
      branch_id: string;
      version: number;
      source_or_target_tombstoned?: boolean;
      /** Zie boven. */
      delete_action_id: string | null;
      placed_on_diagrams: TrashDiagramRef[];
    };

/** Body van een PUT op een mailinstelling (workspace of globaal). */
export interface MailConfigPutBody {
  host: string;
  port: number;
  username: string;
  /** `null` = laat huidige wachtwoord ongewijzigd; `""` = wis. */
  password: string | null;
  from_address: string;
  from_name: string | null;
  transport: "mailchannels" | "smtp";
}

/** De AI-aanmelding van de installatie; nooit de sleutel zelf. */
export interface AiAuthStatusDTO {
  /** Vandaag altijd `anthropic`; andere aanbieders zijn toekomstig werk. */
  provider: string;
  /** Alleen bij GET: de aanbieders die gebouwd zijn. */
  providers?: Array<{ id: string; display_name: string }> | undefined;
  wif: {
    organization_id: string;
    service_account_id: string;
    federation_rule_id: string;
    anthropic_workspace_id: string | null;
  } | null;
  api_key: { last4: string; anthropic_workspace_id?: string | null } | null;
  updated_by: string | null;
  updated_at: string | null;
  last_failure_at: string | null;
  last_failure_reason: string | null;
  /** Alleen bij GET: wat de admin in de Claude Console overneemt. */
  federation?:
    | {
        available: boolean;
        issuer: string | null;
        jwks_url: string | null;
        subject: string;
      }
    | undefined;
}

/** Wire shape voor admin-mail-config-respons (GET + PUT). */
export interface MailConfigPublic {
  host: string;
  port: number;
  username: string;
  password_set: boolean;
  from_address: string;
  from_name: string | null;
  transport: "mailchannels" | "smtp";
  updated_by: string;
  updated_at: string;
}
